import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;        // 200 表示成功,其他值表示失败
  message: string;     // 提示信息
  data: any;           // 返回的数据
  // 以下字段用于内部处理
  success?: boolean;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, code, message, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      // 适配后端格式: code !== 200 表示失败
      if (!success && code !== 200) {
        const error: any = new Error(errorMessage || message);
        error.name = 'BizError';
        error.info = { errorCode: code, errorMessage: errorMessage || message, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 从 localStorage 获取 token 并添加到 query 参数
      const token = localStorage.getItem('token');
      if (token) {
        // 将 token 添加到 URL 参数中
        const url = config.url || '';
        const separator = url.includes('?') ? '&' : '?';
        config.url = `${url}${separator}token=${token}`;
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据,将后端格式转换为项目期望的格式
      const { data } = response;
      
      if (data && typeof data === 'object' && 'code' in data) {
        // 后端格式: { code: 200, message: '', data: {...} }
        // 转换为项目格式: { success: true/false, data: {...}, errorMessage: '' }
        const backendData = data as any;
        response.data = {
          success: backendData.code === 200,
          data: backendData.data,
          errorMessage: backendData.message,
          errorCode: backendData.code,
        } as any;
      }
      
      return response;
    },
  ],
};
